import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocketContext } from '../ws/WebsocketContext';
import { MessageType, WsMessage, WsSubtitle } from '../misc/domain/ws';
import { RoomId } from '../misc/domain';
import {
  WebSpeechService,
  TranscriptResult,
  SpeechRecognitionError,
  RecognitionStatus,
} from '../services/speechRecognition';
import { useAppSelector } from '../app/hooks';
import { userSelector } from '../features/user/userSlice';
import { userFullName } from '../misc/utils/user';

export interface UseInterpreterSubtitlesOptions {
  /** Language for speech recognition (default: 'el-GR') */
  language?: string;
  /** Whether to auto-start when component mounts */
  autoStart?: boolean;
}

export interface UseInterpreterSubtitlesResult {
  /** Whether speech recognition is currently listening */
  isListening: boolean;
  /** Current recognition status */
  status: RecognitionStatus;
  /** Whether speech recognition is supported */
  isSupported: boolean;
  /** Last error that occurred */
  error: SpeechRecognitionError | null;
  /** Start speech recognition */
  start: () => void;
  /** Stop speech recognition */
  stop: () => void;
  /** Toggle speech recognition */
  toggle: () => void;
}

/**
 * Hook for interpreters to capture their speech and send as subtitles.
 * Uses Web Speech API for recognition and WebSocket to broadcast to room.
 *
 * @param roomId - The room ID to broadcast subtitles to
 * @param options - Configuration options
 * @returns Controls and status for speech recognition
 */
/** Random id identifying one transcription session; lets receivers detect
 *  that the sequence counter restarted (page refresh, rejoin, handoff). */
const newSessionId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

export const useInterpreterSubtitles = (
  roomId: RoomId | null,
  options: UseInterpreterSubtitlesOptions = {}
): UseInterpreterSubtitlesResult => {
  const { language = 'el-GR', autoStart = false } = options;

  const { connected, sendMessage } = useWebSocketContext();
  const { user } = useAppSelector(userSelector);

  const serviceRef = useRef<WebSpeechService | null>(null);
  const sequenceRef = useRef(0);
  const sessionIdRef = useRef(newSessionId());
  const sendSubtitleRef = useRef<(result: TranscriptResult) => void>(() => {});

  const [status, setStatus] = useState<RecognitionStatus>(RecognitionStatus.IDLE);
  const [isSupported, setIsSupported] = useState(true);
  const [error, setError] = useState<SpeechRecognitionError | null>(null);

  const isListening = status === RecognitionStatus.LISTENING;

  // Get interpreter info for subtitle messages
  const interpreterId = user?.id ?? '';
  const interpreterName = user ? userFullName(user.token) : 'Interpreter';

  // Update sendSubtitle ref when dependencies change (avoids service recreation)
  useEffect(() => {
    sendSubtitleRef.current = (result: TranscriptResult) => {
      if (!roomId || !connected) return;

      const channel = `/room/${roomId}/subtitles`;
      const subtitle: WsSubtitle = {
        type: 'WsSubtitle',
        interpreter_id: interpreterId,
        interpreter_name: interpreterName,
        text: result.text,
        is_final: result.isFinal,
        confidence: result.confidence,
        language: result.language,
        sequence: sequenceRef.current++,
        session_id: sessionIdRef.current,
        timestamp: result.timestamp,
      };

      const message: WsMessage<WsSubtitle> = {
        kind: MessageType.SEND,
        channel,
        payload: subtitle,
      };

      sendMessage(message);
    };
  }, [roomId, connected, sendMessage, interpreterId, interpreterName]);

  // Initialize speech recognition service (per room; skipped entirely for
  // consumers without a room, e.g. non-interpreters)
  useEffect(() => {
    if (!roomId) {
      return;
    }

    const service = new WebSpeechService({ language });

    if (!service.isSupported()) {
      setIsSupported(false);
      console.warn('[InterpreterSubtitles] Speech recognition not supported');
      return;
    }

    service.onResult((result) => {
      // Use ref to always access latest sendSubtitle function
      sendSubtitleRef.current(result);
    });

    service.onError((err) => {
      console.error('[InterpreterSubtitles] Error:', err);
      setError(err);
    });

    service.onStatusChange(setStatus);

    serviceRef.current = service;

    return () => {
      // destroy() detaches the callbacks before stopping, so no setState
      // can fire after unmount
      service.destroy();
      serviceRef.current = null;
    };
    // The initial language is applied via the setLanguage effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // React to connection changes: pause while disconnected (transcripts could
  // not be delivered and would be silently lost), auto-start once connected.
  useEffect(() => {
    const service = serviceRef.current;
    if (!service) {
      return;
    }

    if (!connected) {
      const recognitionActive =
        service.getStatus() === RecognitionStatus.LISTENING ||
        service.getStatus() === RecognitionStatus.STARTING;
      if (recognitionActive) {
        service.stop();
        setError({
          code: 'NOT_CONNECTED',
          message: 'Connection lost — subtitles paused. Click to restart.',
          recoverable: true,
        });
      }
    } else {
      setError((prev) => (prev?.code === 'NOT_CONNECTED' ? null : prev));
      if (autoStart && roomId && service.getStatus() === RecognitionStatus.IDLE) {
        service.start();
      }
    }
  }, [connected, autoStart, roomId]);

  // Update language if it changes; the service restarts itself when a
  // session is active
  useEffect(() => {
    serviceRef.current?.setLanguage(language);
  }, [language]);

  const start = useCallback(() => {
    if (serviceRef.current && connected && roomId) {
      setError(null);
      serviceRef.current.start();
    } else if (!connected) {
      setError({
        code: 'NOT_CONNECTED',
        message: 'WebSocket not connected',
        recoverable: true,
      });
    } else if (!roomId) {
      setError({
        code: 'NO_ROOM',
        message: 'No room ID provided',
        recoverable: true,
      });
    }
  }, [connected, roomId]);

  const stop = useCallback(() => {
    if (serviceRef.current) {
      serviceRef.current.stop();
    }
  }, []);

  const toggle = useCallback(() => {
    if (isListening) {
      stop();
    } else {
      start();
    }
  }, [isListening, start, stop]);

  return {
    isListening,
    status,
    isSupported,
    error,
    start,
    stop,
    toggle,
  };
};

export default useInterpreterSubtitles;
